import SideBlockAds from "@components/Blocks/Container/SideBlockAds";
import SubSectionNav from "@components/Blocks/SubSectionNav/SubSectionNav";
import { tagsSubSectionMapping } from "@components/Blocks/SubSectionNav/subSectionNavObj";
import { SectionTitleV2 } from "@elements/Typography/TitleV2";
import { Route } from "@route-types/Keyword/+types/Keyword";
import BHBasePage, { BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import {
  fetchContext,
  ResponseType,
  type TRouteWithoutRedirect,
  type TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
// import { UrlMatch } from "@src/app/data";
import { getGTMData, getMetaData } from "@src/app/helper/getMetaData";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { RouteParam } from "@typings/Data";
import { MatchResult } from "path-to-regexp";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import {
  fetchKeywordData,
  type KeywordPayloadResponseType,
} from "./Keyword.server";
import KeywordArticlesList from "./KeywordArticleList";
import KeywordMetaData from "./KeywordMetaData";

export type UrlMatch = {
  urlComponent: MatchResult<RouteParam>;
  variant: string;
};

export function meta({
  loaderData,
}: {
  loaderData: TRouteWithoutRedirect<KeywordPayloadResponseType, string>;
}) {
  const payload = loaderData.payload;
  if (typeof payload !== "object" || !("data" in payload)) return [];
  const keywordArticles = payload.data;
  const tagAlias = payload.tagAlias;
  const tagName =
    keywordArticles[0]?.tags?.find(
      (tag) => tag.alias?.includes(tagAlias) && tag.name,
    )?.name ?? "";
  return [getMetaData(KeywordMetaData({ tagName, tagAlias }).metaTags)];
}

export default function KeywordPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    KeywordPayloadResponseType,
    string
  > = useLoaderData();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  /* eslint-disable @typescript-eslint/no-unnecessary-condition */
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const keywordArticles = dataLoaderResponse.payload.data;
    const tagAlias = dataLoaderResponse.payload.tagAlias;
    const articleCount = dataLoaderResponse.payload.totalArticlesCount;
    const sectionName = "tags";
    let tagName = "";
    let tagId = "";

    for (const tag of keywordArticles[0].tags || []) {
      if (tag.alias?.includes(tagAlias)) {
        if (tag.name !== undefined) {
          tagName = tag.name;
          tagId = tag.alias ?? "";
          break;
        }
      }
    }

    const pageAdTargetValue = PageAdTargetingTypeEnum.TAG;
    const keywordSlotTarget: AdTargetingType<ArticleAdTargetKeys, string>[] = [
      { key: ArticleAdTargetKeys.TAGS, value: tagName.toLowerCase() },
    ];

    const { displaySectionName, uniqueSectionName } =
      tagsSubSectionMapping[tagName] || {};

    const basePageProps: BHBasePageProps = {
      uniqueSectionName: sectionName,
      pageAdTargetValue: pageAdTargetValue,
      ...(displaySectionName && {
        hideScrollableSectionMenu: true,
      }),
      adsSlotTarget: keywordSlotTarget,
      hideNavigationInMobile: true,
    };

    return (
      <BHBasePage {...basePageProps}>
        {displaySectionName && (
          <WidgetWrapper className={"mb-3"}>
            <SectionTitleV2 sectionName={displaySectionName} />
            <SubSectionNav
              sectionName={uniqueSectionName}
              displaySection={displaySectionName || ""}
            />
          </WidgetWrapper>
        )}
        <div className="lg:max-w-screen-lg xl:max-w-screen-bh-cxl m-auto px-[10px] lg:px-[5px] flex flex-col lg:flex-row w-full gap-4">
          <KeywordArticlesList
            articles={keywordArticles}
            tagAlias={tagAlias}
            tagName={tagName}
            tagId={tagId}
            articleCount={articleCount}
            sectionName={sectionName}
            adsSlotTarget={keywordSlotTarget}
          />
          <SideBlockAds
            adsSectionName={sectionName}
            adsOnly={true}
            pageAdTargetingProps={pageAdTargetValue}
            articleSlotTarget={keywordSlotTarget}
            className="flex-1"
          />
        </div>
      </BHBasePage>
    );
  }
}

export async function loader({ request, params }: Route.LoaderArgs) {
  // Parse URL with localhost base to handle both relative paths ("/section") and absolute URLs ("https://localhost:5174/section")
  const url = new URL(request.url, `http://localhost`);
  const tagAlias = url.pathname;
  const tagName = url.pathname.replace("/tags/", "");

  const data = await fetchKeywordData({ request, params } as Route.LoaderArgs);
  const { gaData } = KeywordMetaData({ tagName, tagAlias });

  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<KeywordPayloadResponseType, string>
  >;
}
